'use client';

import { useMemo } from 'react';
import { MenuItem } from '@headlessui/react';
import classNames from 'classnames';
import { Variant } from 'shared/types/product';
import { useFormat } from 'helpers/hooks/useFormat';
import { discardRepeatedValues } from './helpers/discardRepeatedValues';
import { filterAttributeBasedVariants } from './helpers/filterAttributeBasedVariants';
import Picker from './Picker';

type Props = {
  currentVariant: Variant;
  variants: Variant[];
  onClick?: (sku: string) => void;
};

const ATTRIBUTE = 'size';
const ATTRIBUTE_TO_FILTER_TO = 'colour';

const SizePicker: React.FC<Props> = ({ currentVariant, variants, onClick }) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });
  const variantsToUse = useMemo(() => {
    const currentValue = currentVariant.attributes?.[ATTRIBUTE];
    const result = currentValue
      ? filterAttributeBasedVariants(variants, currentVariant, ATTRIBUTE_TO_FILTER_TO)
      : discardRepeatedValues(variants, ATTRIBUTE);
    const filteredVariants: Variant[] = [...(result || [])];
    return [...new Set(variants.map((x) => x.attributes?.[ATTRIBUTE]).filter(Boolean))].sort().map((attribute) => {
      const target = filteredVariants.find((x) => x.attributes?.[ATTRIBUTE] === attribute);
      return {
        available: !!target,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        variant: target || variants.find((x) => x.attributes?.[ATTRIBUTE] === attribute)!,
      };
    });
  }, [currentVariant, variants]);

  return (
    <Picker
      label={
        <span className="grow text-start font-semibold">
          <span className="capitalize group-data-[open]:hidden">{currentVariant.attributes?.[ATTRIBUTE]}</span>
          <span className="hidden group-data-[open]:inline">
            {formatMessage({ id: 'pdp.pickSize', defaultMessage: 'Seleziona taglia' })}
          </span>
        </span>
      }
      itemsClassName="grid grid-cols-5 gap-3 p-3 lg:grid-cols-3"
    >
      <>
        {variantsToUse.map(({ available, variant: { attributes, id, sku } }) => (
          <MenuItem key={id}>
            <button
              onClick={() => onClick?.(sku)}
              className={classNames('block rounded-lg border-2 py-4 text-center text-[14px] font-semibold', {
                'border-neutral-700 !bg-black text-white': id === currentVariant.id,
                'border-neutral-300 hover:bg-neutral-50': id !== currentVariant.id,
                'border-dashed text-neutral-500': !available,
              })}
            >
              {attributes?.[ATTRIBUTE]?.toString()}
            </button>
          </MenuItem>
        ))}
      </>
    </Picker>
  );
};

export default SizePicker;
